import { Controller, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import Button from 'components/Button/Button';
import DraggableList from 'components/DraggableList/DraggableList';
import IconButton from 'components/IconButton/IconButton';
import TextField from 'components/TextField/TextField';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { SURVEY_QUESTION_TYPES } from 'constants/materialAssessment';

import CreateSurveyQuestionSelectOption from './CreateSurveyQuestionSelectOption';

import type { FieldArrayWithId } from 'react-hook-form';
import type { ICreateSurveyForm } from '../interfaces/ICreateSurvey';
import type { ICreateSurveyQuestionOptions } from '../interfaces/ICreateSurveyQuestionOptions';

import styles from '../scss/CreateSurveyOptions.module.scss';

type FieldItem = FieldArrayWithId<ICreateSurveyForm, `questions.${number}.options`, 'id'>;

function CreateSurveyQuestionOptions({
  control,
  questionIndex,
  fields,
  answerType,
  onAddOption,
  onRemoveOption,
  onMoveOption,
}: ICreateSurveyQuestionOptions) {
  const { t } = useTranslation();

  const { errors } = useFormState({ control });
  const errorMessage = errors?.questions?.[questionIndex]?.options?.message || null;

  const renderPrioritiseOption = (_item: FieldItem, index: number) => (
    <>
      <Controller
        control={control}
        name={`questions.${questionIndex}.options.${index}.name`}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <>
            <TextField
              name={name}
              value={value}
              placeholder={`Option ${index + 1}`}
              className={styles.textField}
              onChange={(event) => onChange(event.target.value)}
              required
            />
            {error ? <p className={styles.error}>{error?.message}</p> : null}
          </>
        )}
      />
      <IconButton
        icon={<CloseIcon />}
        onClick={() => onRemoveOption(index)}
        className={styles.deleteOptionButton}
      />
    </>
  );

  return (
    <div className={styles.optionsWrapper}>
      {answerType === SURVEY_QUESTION_TYPES.PRIORITIZE ? (
        <DraggableList
          items={fields}
          onMove={onMoveOption}
          renderItem={renderPrioritiseOption}
          itemClassName={styles.prioritiseItem}
          itemType="PRIORITISE_OPTION"
          keyExtractor={(field: FieldItem) => field.id}
        />
      ) : (
        fields.map((field, index) => (
          <CreateSurveyQuestionSelectOption
            key={field.id}
            control={control}
            questionIndex={questionIndex}
            index={index}
            onRemoveOption={onRemoveOption}
          />
        ))
      )}
      {errorMessage ? <p className={styles.error}>{errorMessage}</p> : null}
      <Button
        title={t('materialityAssessment.addOption')}
        className={styles.addOptionButton}
        startIcon={<PlusIcon />}
        variant={BUTTON_VARIANT.ghost}
        onClick={onAddOption}
      />
    </div>
  );
}

export default CreateSurveyQuestionOptions;
